/**
 * /admin/early-access — "Story Behind the Resume" campaign dashboard.
 * Stats + structured Q&A per submission, wall curation (pick which answer
 * becomes the public quote), one-click "Invite to beta", source filters, delete.
 */
import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import AdminHubNav from "../components/AdminHubNav";
import { api, friendlyAxiosError } from "../lib/api";
import { Loader, RefreshCw, Trash2, Send, Linkedin, ChevronDown, ChevronUp, CheckCircle2, Mail, Sparkles, EyeOff, Flame, Copy, Check, FileText, Lock } from "lucide-react";
import { toast } from "sonner";

const NAVY = "#0F1B2D";
const YELLOW = "#FFC145";

function StatCard({ label, value, testid }) {
  return (
    <div className="rm-card-static p-4" data-testid={testid}>
      <div className="text-2xl font-black" style={{ color: NAVY }}>{value}</div>
      <div className="font-mono-rm text-[10px] uppercase tracking-[2px] mt-1" style={{ color: "#64748B" }}>{label}</div>
    </div>
  );
}

function StoryRow({ s, labels, quoteFields, onInvite, onDelete, onWall, inviting, walling }) {
  const [open, setOpen] = useState(false);
  const [quoteField, setQuoteField] = useState(s.wall_quote_field || "resume_gap");
  const a = s.answers || {};
  return (
    <div className="rm-card-static p-4" data-testid={`early-access-story-${s.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-bold text-sm" style={{ color: NAVY }}>{s.name}</span>
        <span className="font-mono-rm text-xs" style={{ color: "#64748B" }}>{s.email}</span>
        {a.career && (
          <span className="rm-tag text-[10px]" style={{ background: YELLOW, borderColor: NAVY, color: NAVY }}>
            {a.career.length > 40 ? a.career.slice(0, 40) + "…" : a.career}
          </span>
        )}
        {s.source && (
          <span className="font-mono-rm text-[10px] px-2 py-0.5 rounded-full border"
                style={{ borderColor: "#CBD5E1", color: "#64748B" }}
                data-testid={`early-access-source-${s.id}`}>
            src: {s.source}
          </span>
        )}
        {s.wants_early_access && (
          <span className="inline-flex items-center gap-1 rm-tag text-[10px]"
                style={{ background: "#FFEDD5", borderColor: "#C2410C", color: "#C2410C" }}
                data-testid={`early-access-wants-${s.id}`}>
            <Flame size={11} /> Wants access
          </span>
        )}
        {s.invited && (
          <span className="inline-flex items-center gap-1 rm-tag text-[10px]"
                style={{ background: "#DCFCE7", borderColor: "#0E5A4A", color: "#0E5A4A" }}
                data-testid={`early-access-invited-${s.id}`}>
            <CheckCircle2 size={11} /> Invited
          </span>
        )}
        {s.wall_approved && (
          <span className="inline-flex items-center gap-1 rm-tag text-[10px]"
                style={{ background: "#FEF3C7", borderColor: "#CA8A04", color: "#92400E" }}
                data-testid={`early-access-on-wall-${s.id}`}>
            <Sparkles size={11} /> On wall
          </span>
        )}
        {s.confirmation_sent && (
          <span className="inline-flex items-center gap-1 text-[10px] font-mono-rm" style={{ color: "#94A3B8" }}>
            <Mail size={11} /> confirmed
          </span>
        )}
        <span className="ml-auto font-mono-rm text-[10px]" style={{ color: "#94A3B8" }}>
          {(s.created_at || "").slice(0, 10)}
        </span>
      </div>

      {/* Preview: about + resume_gap always visible; full Q&A on expand */}
      <div className="mt-2 space-y-2">
        {(open ? Object.keys(labels) : ["about", "resume_gap"]).map((k) =>
          a[k] ? (
            <div key={k}>
              <div className="font-mono-rm text-[9px] uppercase tracking-[2px]" style={{ color: "#94A3B8" }}>
                {labels[k]}
              </div>
              <p className="text-sm whitespace-pre-wrap" style={{ color: "#334155" }}>
                {!open && a[k].length > 160 ? a[k].slice(0, 160) + "…" : a[k]}
              </p>
            </div>
          ) : null
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-3">
        <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 text-xs font-bold" style={{ color: NAVY }}
                data-testid={`early-access-expand-${s.id}`}>
          {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {open ? "Less" : "All answers"}
        </button>
        {s.linkedin_url && (
          <a href={s.linkedin_url.startsWith("http") ? s.linkedin_url : `https://${s.linkedin_url}`}
             target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1 text-xs font-bold" style={{ color: "#0A66C2" }}>
            <Linkedin size={13} /> LinkedIn
          </a>
        )}
        {!s.share_ok && (
          <span className="inline-flex items-center gap-1 text-[10px] font-mono-rm uppercase tracking-wide" style={{ color: "#B91C1C" }}
                title="They didn't tick the share box — story can't go on the wall.">
            <EyeOff size={11} /> no share consent
          </span>
        )}
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {s.share_ok && (
            <>
              {!s.wall_approved && (
                <select value={quoteField} onChange={(e) => setQuoteField(e.target.value)}
                        className="border-2 text-[11px] font-bold px-2 py-1.5 bg-white"
                        style={{ borderColor: NAVY, color: NAVY }}
                        data-testid={`early-access-quote-select-${s.id}`}>
                  {quoteFields.filter((f) => a[f]).map((f) => (
                    <option key={f} value={f}>Quote: {labels[f]}</option>
                  ))}
                </select>
              )}
              <button onClick={() => onWall(s, quoteField)} disabled={walling === s.id}
                      className="rm-btn text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
                      data-testid={`early-access-wall-btn-${s.id}`}>
                {walling === s.id ? <Loader size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {s.wall_approved ? "Remove from wall" : "Feature on wall"}
              </button>
            </>
          )}
          {!s.invited && (
            <button onClick={() => onInvite(s)} disabled={inviting === s.id}
                    className="rm-btn text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
                    data-testid={`early-access-invite-btn-${s.id}`}>
              {inviting === s.id ? <Loader size={12} className="animate-spin" /> : <Send size={12} />}
              Invite to beta
            </button>
          )}
          <button onClick={() => onDelete(s)}
                  className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1.5"
                  style={{ color: "#B91C1C" }}
                  data-testid={`early-access-delete-btn-${s.id}`}>
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

function PostKit() {
  const [weeks, setWeeks] = useState(null);
  const [open, setOpen] = useState(false);
  const [copiedWeek, setCopiedWeek] = useState(null);

  const load = async () => {
    try {
      const res = await api.get("/admin/challenge/post-kit");
      setWeeks(res.data?.weeks || []);
    } catch { /* non-fatal */ }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !weeks) load();
  };

  const copy = async (w) => {
    try {
      await navigator.clipboard.writeText(w.copy);
      setCopiedWeek(w.week_number);
      toast.success(`Week ${w.week_number} copy copied — paste into LinkedIn`);
      setTimeout(() => setCopiedWeek(null), 2500);
    } catch {
      toast.error("Couldn't copy — select the text manually.");
    }
  };

  return (
    <div className="rm-card-static p-4 mb-4" data-testid="post-kit-panel">
      <button onClick={toggle} className="flex items-center gap-2 w-full text-left" data-testid="post-kit-toggle">
        <FileText size={16} style={{ color: "#FF6A00" }} />
        <div className="min-w-0">
          <div className="font-mono-rm text-[10px] uppercase tracking-[2px] font-bold" style={{ color: "#FF6A00" }}>
            Weekly Post Kit
          </div>
          <div className="text-sm font-bold" style={{ color: NAVY }}>
            Ready-to-paste LinkedIn copy for all 5 SPARK weeks — links pre-tagged
          </div>
        </div>
        {open ? <ChevronUp size={16} className="ml-auto" style={{ color: NAVY }} /> : <ChevronDown size={16} className="ml-auto" style={{ color: NAVY }} />}
      </button>

      {open && (
        <div className="mt-4 space-y-3" data-testid="post-kit-weeks">
          {!weeks ? (
            <div className="flex items-center gap-2 py-4 justify-center font-mono-rm text-xs uppercase tracking-widest" style={{ color: "#64748B" }}>
              <Loader size={14} className="animate-spin" /> Loading…
            </div>
          ) : weeks.map((w) => (
            <div key={w.week_number} className="border-2 p-3" style={{ borderColor: NAVY, background: "#FFFDF8" }}
                 data-testid={`post-kit-week-${w.week_number}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="rm-tag text-[10px]" style={{ background: YELLOW, borderColor: NAVY, color: NAVY }}>
                  Week {w.week_number} · {w.spark_letter}
                </span>
                <span className="font-black text-sm" style={{ color: NAVY }}>{w.label}</span>
                <button onClick={() => copy(w)}
                        className="ml-auto rm-btn text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
                        data-testid={`post-kit-copy-${w.week_number}`}>
                  {copiedWeek === w.week_number ? <Check size={12} /> : <Copy size={12} />}
                  {copiedWeek === w.week_number ? "Copied!" : "Copy"}
                </button>
              </div>
              <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed" style={{ color: "#334155" }}>{w.copy}</pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PrivateModePanel() {
  const [cfg, setCfg] = useState(null);
  const [code, setCode] = useState("");
  const [launch, setLaunch] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/private-mode");
      setCfg(r.data);
      setCode(r.data.access_code);
      setLaunch(r.data.launch_date || "");
    } catch { /* non-fatal */ }
  };
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/admin/private-mode", { enabled: !cfg.enabled });
      setCfg((c) => ({ ...c, enabled: r.data.enabled }));
      toast.success(r.data.enabled
        ? "Private Mode ON — visitors now see the Coming Soon splash"
        : "Private Mode OFF — the site is open to the public");
    } catch (e) {
      toast.error(friendlyAxiosError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveCode = async () => {
    if (!code.trim() || code.trim().toUpperCase() === cfg.access_code) return;
    setSaving(true);
    try {
      const r = await api.patch("/admin/private-mode", { access_code: code.trim() });
      setCfg((c) => ({ ...c, access_code: r.data.access_code }));
      setCode(r.data.access_code);
      toast.success(`Access code changed to ${r.data.access_code}`);
    } catch (e) {
      toast.error(friendlyAxiosError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveLaunch = async (value) => {
    setSaving(true);
    try {
      const r = await api.patch("/admin/private-mode", { launch_date: value });
      setCfg((c) => ({ ...c, launch_date: r.data.launch_date }));
      setLaunch(r.data.launch_date || "");
      toast.success(r.data.launch_date
        ? `Splash countdown set — launching ${r.data.launch_date}`
        : "Countdown removed from the splash");
    } catch (e) {
      toast.error(friendlyAxiosError(e));
    } finally {
      setSaving(false);
    }
  };

  const blastLaunch = async () => {
    if (!window.confirm("Send the launch announcement email to everyone on the notify list? Only people who haven't received it yet will get it.")) return;
    setSaving(true);
    try {
      const r = await api.post("/admin/private-mode/blast-launch");
      if (r.data?.ok) {
        toast.success(r.data.queued > 0
          ? `Launch email queued for ${r.data.queued} people (${r.data.already_sent} already had it)`
          : `Everyone on the list already received it (${r.data.already_sent} sent)`);
        await load();
      } else {
        toast.error(r.data?.detail || "Couldn't start the blast.");
      }
    } catch (e) {
      toast.error(friendlyAxiosError(e));
    } finally {
      setSaving(false);
    }
  };

  const copyEmails = async () => {
    try {
      await navigator.clipboard.writeText((cfg.notify_emails || []).map((e) => e.email).join(", "));
      toast.success("Notify-list emails copied");
    } catch {
      toast.error("Couldn't copy — clipboard blocked");
    }
  };

  if (!cfg) return null;
  return (
    <div className="rm-card-static p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="private-mode-panel">
      <Lock size={16} style={{ color: "#FF6A00" }} />
      <div className="min-w-0">
        <div className="font-mono-rm text-[10px] uppercase tracking-[2px] font-bold" style={{ color: "#FF6A00" }}>
          Private Mode · Coming Soon splash
        </div>
        <div className="text-sm font-bold" style={{ color: NAVY }}>
          {cfg.enabled
            ? "ON — visitors see the splash. Only /early-access, logged-in users & code holders get through."
            : "OFF — the whole site is public."}
        </div>
      </div>
      <div className="ml-auto flex items-center gap-3 flex-wrap">
        <span className="font-mono-rm text-[10px] uppercase tracking-wide" style={{ color: "#64748B" }}
              data-testid="private-mode-notify-count">
          {cfg.notify_count} launch-notify email{cfg.notify_count === 1 ? "" : "s"}
        </span>
        {cfg.notify_count > 0 && (
          <button onClick={copyEmails} className="rm-btn text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
                  data-testid="private-mode-copy-emails">
            <Copy size={12} /> Copy list
          </button>
        )}
        {cfg.notify_count > 0 && (
          <button onClick={blastLaunch} disabled={saving}
                  className="rm-btn text-xs px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-60"
                  title="Emails the launch announcement to everyone on the notify list who hasn't received it yet"
                  data-testid="private-mode-blast-btn">
            <Send size={12} /> Launch blast{cfg.blast_sent > 0 ? ` (${cfg.blast_sent} sent)` : ""}
          </button>
        )}
        <div className="flex items-center gap-1.5" title="Launch date — shows a live countdown on the splash">
          <input type="date" value={launch} onChange={(e) => setLaunch(e.target.value)}
                 className="border-2 text-[11px] font-bold px-2 py-1.5 bg-white font-mono-rm"
                 style={{ borderColor: NAVY, color: NAVY }}
                 data-testid="private-mode-launch-input" />
          {launch !== (cfg.launch_date || "") && (
            <button onClick={() => saveLaunch(launch)} disabled={saving}
                    className="rm-btn text-xs px-2.5 py-1.5" data-testid="private-mode-launch-save">
              Set
            </button>
          )}
          {cfg.launch_date && launch === cfg.launch_date && (
            <button onClick={() => saveLaunch("")} disabled={saving}
                    className="text-[10px] font-bold underline" style={{ color: "#64748B" }}
                    data-testid="private-mode-launch-clear">
              clear
            </button>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                 className="border-2 text-[11px] font-bold px-2 py-1.5 bg-white font-mono-rm uppercase tracking-widest w-32"
                 style={{ borderColor: NAVY, color: NAVY }}
                 data-testid="private-mode-code-input" />
          {code.trim().toUpperCase() !== cfg.access_code && (
            <button onClick={saveCode} disabled={saving}
                    className="rm-btn text-xs px-2.5 py-1.5" data-testid="private-mode-code-save">
              Save
            </button>
          )}
        </div>
        <button onClick={toggle} disabled={saving}
                className="rm-tag text-[10px] cursor-pointer font-bold"
                style={{
                  background: cfg.enabled ? "#FEE2E2" : "#DCFCE7",
                  borderColor: cfg.enabled ? "#B91C1C" : "#0E5A4A",
                  color: cfg.enabled ? "#B91C1C" : "#0E5A4A",
                }}
                title="Controls the site-wide Coming Soon splash"
                data-testid="private-mode-toggle">
          {cfg.enabled ? "PRIVATE — click to open to the world" : "PUBLIC — click to go private"}
        </button>
      </div>
    </div>
  );
}

export default function EarlyAccessAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [srcFilter, setSrcFilter] = useState("");
  const [inviting, setInviting] = useState(null);
  const [walling, setWalling] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [autoPicking, setAutoPicking] = useState(false);

  const autoPick = async () => {
    if (!window.confirm("Let AI pick the best consented stories and feature them on the public wall? (Manually featured stories are kept.)")) return;
    setAutoPicking(true);
    try {
      const res = await api.post("/admin/early-access/auto-pick", { max_picks: 6 });
      if (res.data?.ok) {
        toast.success(`Featured ${res.data.picked_count} stories on the wall (${res.data.newly_added} newly added)`);
        await reload();
      } else {
        toast.error(res.data?.detail || "Nothing to pick yet.");
      }
    } catch (e) {
      toast.error(friendlyAxiosError(e));
    } finally {
      setAutoPicking(false);
    }
  };

  const loadChallenge = async () => {
    try {
      const res = await api.get("/admin/challenge");
      setChallenge(res.data);
    } catch { /* non-fatal */ }
  };

  const setOverride = async (value) => {
    try {
      await api.patch("/admin/challenge", { override_theme: value || null });
      toast.success(value ? `Challenge pinned to ${value}` : "Challenge back on auto-rotation");
      await loadChallenge();
    } catch (e) {
      toast.error(friendlyAxiosError(e));
    }
  };

  const toggleVisible = async () => {
    const next = !challenge?.current?.visible;
    try {
      await api.patch("/admin/challenge", { visible: next });
      toast.success(next ? "Challenge page is LIVE — teaser now shows on the story page" : "Challenge page hidden — story page shows no features");
      await loadChallenge();
    } catch (e) {
      toast.error(friendlyAxiosError(e));
    }
  };

  const sendChallengeInvite = async () => {
    if (!window.confirm("Email this week's challenge to every story signup who hasn't received it yet? This also makes the challenge page live.")) return;
    try {
      const res = await api.post("/admin/early-access/send-challenge-invite");
      if (res.data?.ok) {
        toast.success(`Sending to ${res.data.queued} signups (${res.data.already_sent} already had it). Challenge page is now live.`);
        await loadChallenge();
      } else {
        toast.error(res.data?.detail || "Couldn't start the send.");
      }
    } catch (e) {
      toast.error(friendlyAxiosError(e));
    }
  };

  const reload = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await api.get("/admin/early-access");
      setData(res.data);
    } catch (e) {
      setErr(friendlyAxiosError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); loadChallenge(); }, []);

  const invite = async (s) => {
    setInviting(s.id);
    try {
      await api.post("/admin/beta/invites", {
        email: s.email,
        label: "Story campaign",
        note: `Story Behind the Resume (${s.source || "direct"})`,
        auto_send: true,
      });
      toast.success(`Beta invite sent to ${s.email}`);
      await reload();
    } catch (e) {
      toast.error(friendlyAxiosError(e));
    } finally {
      setInviting(null);
    }
  };

  const toggleWall = async (s, quoteField) => {
    setWalling(s.id);
    try {
      await api.patch(`/admin/early-access/${s.id}/wall`, {
        approved: !s.wall_approved,
        quote_field: quoteField,
      });
      toast.success(s.wall_approved ? "Removed from the story wall" : "Featured on the story wall");
      await reload();
    } catch (e) {
      toast.error(friendlyAxiosError(e));
    } finally {
      setWalling(null);
    }
  };

  const sendDigest = async () => {
    try {
      const res = await api.post("/admin/early-access/send-digest");
      if (res.data?.ok) {
        toast.success(`Digest sent to ${res.data.sent_to} — ${res.data.new_stories} new stories this week`);
      } else {
        toast.error(res.data?.detail || "Digest couldn't be sent.");
      }
    } catch (e) {
      toast.error(friendlyAxiosError(e));
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.name}'s story + signup (${s.email})? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/early-access/${s.id}`);
      toast.success("Deleted");
      await reload();
    } catch (e) {
      toast.error(friendlyAxiosError(e));
    }
  };

  const stats = data?.stats;
  const labels = stats?.question_labels || {};
  const quoteFields = stats?.quote_fields || [];
  const stories = (data?.stories || []).filter((s) => !srcFilter || (s.source || "direct") === srcFilter);

  return (
    <div className="min-h-screen" style={{ background: "#F6F1E7" }}>
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-8" data-testid="early-access-admin-page">
        <AdminHubNav active="early-access" />
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black" style={{ color: NAVY }}>The Story Behind the Resume</h1>
            <p className="text-sm mt-1" style={{ color: "#64748B" }}>
              Stories + early-access signups from the /early-access campaign page.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={autoPick} disabled={autoPicking}
                    className="rm-btn text-xs px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-60"
                    data-testid="early-access-autopick-btn">
              <Sparkles size={13} /> {autoPicking ? "Picking…" : "Auto-pick wall stories"}
            </button>
            <button onClick={sendDigest} className="rm-btn text-xs px-3 py-2 inline-flex items-center gap-1.5" data-testid="early-access-digest-btn">
              <Mail size={13} /> Email me this week's digest
            </button>
            <button onClick={reload} className="rm-btn text-xs px-3 py-2 inline-flex items-center gap-1.5" data-testid="early-access-refresh-btn">
              <RefreshCw size={13} /> Refresh
            </button>
          </div>
        </div>

        <PrivateModePanel />

        <PostKit />


        {challenge && (
          <div className="rm-card-static p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="early-access-challenge-admin">
            <Flame size={16} style={{ color: "#FF6A00" }} />
            <div className="min-w-0">
              <div className="font-mono-rm text-[10px] uppercase tracking-[2px] font-bold" style={{ color: "#FF6A00" }}>
                Weekly Career Challenge · Week {challenge.current.week_number}
              </div>
              <div className="text-sm font-bold" style={{ color: NAVY }}>
                {challenge.current.label} {challenge.current.overridden ? "(pinned)" : "(auto-rotating)"}
              </div>
            </div>
            <div className="ml-auto flex items-center gap-3 flex-wrap">
              <span className="font-mono-rm text-[10px] uppercase tracking-wide" style={{ color: "#64748B" }}
                    data-testid="early-access-decoder-runs">
                Decoder runs: {challenge.decoder_runs_7d} this week · {challenge.decoder_runs_total} total
                {challenge.shared_briefs != null && (
                  <> · Shared: {challenge.shared_briefs} briefs / {challenge.shared_views} views</>
                )}
              </span>
              <button onClick={toggleVisible}
                      className="rm-tag text-[10px] cursor-pointer font-bold"
                      style={{
                        background: challenge.current.visible ? "#DCFCE7" : "#FEE2E2",
                        borderColor: challenge.current.visible ? "#0E5A4A" : "#B91C1C",
                        color: challenge.current.visible ? "#0E5A4A" : "#B91C1C",
                      }}
                      title="Controls the /challenge page and the teaser on the story page"
                      data-testid="early-access-challenge-visible-toggle">
                {challenge.current.visible ? "LIVE — click to hide" : "HIDDEN — click to go live"}
              </button>
              <button onClick={sendChallengeInvite}
                      className="rm-btn text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
                      data-testid="early-access-send-challenge-btn">
                <Send size={12} /> Email week {challenge.current.week_number} challenge to signups
              </button>
              <select value={challenge.current.overridden ? challenge.current.theme : ""}
                      onChange={(e) => setOverride(e.target.value)}
                      className="border-2 text-[11px] font-bold px-2 py-1.5 bg-white"
                      style={{ borderColor: NAVY, color: NAVY }}
                      data-testid="early-access-challenge-override">
                <option value="">Auto-rotate</option>
                {(challenge.themes || []).map((t) => (
                  <option key={t} value={t}>Pin: {t}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 py-12 justify-center font-mono-rm text-xs uppercase tracking-widest" style={{ color: "#64748B" }}>
            <Loader size={14} className="animate-spin" /> Loading…
          </div>
        ) : err ? (
          <div className="rm-card-static p-4 text-sm font-bold" style={{ color: "#B91C1C" }} data-testid="early-access-admin-error">{err}</div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mb-4">
              <StatCard label="Total stories" value={stats.total} testid="early-access-stat-total" />
              <StatCard label="Last 7 days" value={stats.last_7_days} testid="early-access-stat-week" />
              <StatCard label="Want access" value={stats.wants_early_access} testid="early-access-stat-wants" />
              <StatCard label="Invited to beta" value={stats.invited} testid="early-access-stat-invited" />
              <StatCard label="Emails confirmed" value={stats.emails_sent} testid="early-access-stat-emails" />
              <StatCard label="On story wall" value={stats.on_wall || 0} testid="early-access-stat-wall" />
            </div>

            {Object.keys(stats.by_source || {}).length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="early-access-source-filters">
                <span className="font-mono-rm text-[10px] uppercase tracking-[2px] font-bold" style={{ color: "#64748B" }}>
                  Source:
                </span>
                {Object.entries(stats.by_source).map(([src, n]) => (
                  <button key={src} onClick={() => setSrcFilter(srcFilter === src ? "" : src)}
                          className="rm-tag text-[10px] cursor-pointer font-mono-rm"
                          style={{ background: srcFilter === src ? NAVY : "white", color: srcFilter === src ? YELLOW : "#475569", borderColor: "#94A3B8" }}
                          data-testid={`early-access-source-filter-${src}`}>
                    {src} · {n}
                  </button>
                ))}
              </div>
            )}

            {stories.length === 0 ? (
              <div className="rm-card-static p-8 text-center text-sm" style={{ color: "#64748B" }} data-testid="early-access-empty">
                No stories yet. Share <span className="font-mono-rm font-bold">/early-access?src=your-post-tag</span> in your LinkedIn posts to start collecting.
              </div>
            ) : (
              <div className="space-y-3" data-testid="early-access-story-list">
                {stories.map((s) => (
                  <StoryRow key={s.id} s={s} labels={labels} quoteFields={quoteFields}
                            onInvite={invite} onDelete={remove} onWall={toggleWall}
                            inviting={inviting} walling={walling} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
